import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DealerManager } from "@/components/admin/DealerManager";
import { TranslationDraftButton } from "@/components/admin/TranslationDraftButton";
import { requestTranslationDraft } from "@/lib/adminTranslation";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const emptyFaq = { question: "", answer: "", order: 0, translations: {} };
const languages = [{ id: "tr", label: "Türkçe" }, { id: "en", label: "English" }, { id: "ro", label: "Română" }, { id: "fr", label: "Français" }, { id: "ar", label: "العربية" }, { id: "de", label: "Deutsch" }, { id: "it", label: "Italiano" }, { id: "es", label: "Español" }, { id: "pt", label: "Português" }, { id: "ru", label: "Русский" }, { id: "az", label: "Azərbaycanca" }, { id: "fa", label: "فارسی" }, { id: "zh", label: "中文" }];

const localizedFaq = (faq, language) => language === "tr" ? faq : { ...faq, question: faq.translations?.[language]?.question || "", answer: faq.translations?.[language]?.answer || "" };

export const DealerFaqManager = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [faqs, setFaqs] = useState([]);
  const [faqLanguage, setFaqLanguage] = useState("tr");
  const [newFaq, setNewFaq] = useState(emptyFaq);
  const [activeTab, setActiveTab] = useState("dealers");

  const reload = async () => {
    const faqData = await axios.get(`${API}/admin/faqs`, { headers });
    setFaqs(faqData.data.faqs);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const updateFaqField = (faq, field, value) => setFaqs((current) => current.map((item) => {
    if (item.id !== faq.id) return item;
    if (faqLanguage === "tr") return { ...item, [field]: value };
    return { ...item, translations: { ...(item.translations || {}), [faqLanguage]: { ...(item.translations?.[faqLanguage] || {}), [field]: value } } };
  }));
  const saveFaq = async (faq) => { await axios.patch(`${API}/admin/faqs/${faq.id}`, faq, { headers }); toast.success("Soru tüm dil kayıtlarıyla kaydedildi."); };
  const translateFaq = async (faq) => {
    const result = await requestTranslationDraft({
      token,
      resourceType: "faq",
      source: { question: faq.question, answer: faq.answer },
      translations: faq.translations || {},
    });
    setFaqs((current) => current.map((item) => item.id === faq.id ? { ...item, translations: result.translations } : item));
    return result;
  };
  const translateNewFaq = async () => {
    const result = await requestTranslationDraft({
      token,
      resourceType: "faq",
      source: { question: newFaq.question, answer: newFaq.answer },
      translations: newFaq.translations || {},
    });
    setNewFaq((current) => ({ ...current, translations: result.translations }));
    return result;
  };
  const deleteFaq = async (id) => { if (!window.confirm("Bu soruyu silmek istiyor musunuz?")) return; await axios.delete(`${API}/admin/faqs/${id}`, { headers }); await reload(); };
  const addFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) { toast.error("Önce Türkçe soru ve cevabı yazın."); return; }
    await axios.post(`${API}/admin/faqs`, { ...newFaq, order: faqs.length }, { headers });
    setNewFaq(emptyFaq); await reload(); toast.success("Soru eklendi. Diğer dilleri listeden düzenleyebilirsiniz.");
  };

  return <section>
    <div><p className="text-xs font-bold uppercase tracking-[.16em] text-brand">İş ortaklığı yönetimi</p><h1 className="mt-3 font-heading text-3xl font-bold">Bayiler ve SSS</h1><p className="mt-2 max-w-2xl text-sm text-ink/55">Bayi konumlarını ve sık sorulan soruları ayrı çalışma alanlarından yönetin.</p></div>
    <div role="tablist" aria-label="Bayiler ve SSS" className="mt-6 inline-flex rounded-2xl border border-detail bg-surface p-1"><button type="button" role="tab" aria-selected={activeTab === "dealers"} onClick={() => setActiveTab("dealers")} className={`rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "dealers" ? "bg-ink text-cream" : "text-ink/60"}`}>Bayiler</button><button type="button" role="tab" aria-selected={activeTab === "faqs"} onClick={() => setActiveTab("faqs")} className={`rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "faqs" ? "bg-ink text-cream" : "text-ink/60"}`}>SSS</button></div>
    {activeTab === "dealers" && <div className="mt-6"><DealerManager token={token} /></div>}
    {activeTab === "faqs" && <div className="mt-7"><div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-heading text-2xl font-bold">Sıkça sorulan sorular</h2><p className="mt-1 text-sm text-ink/55">Dili seçin; her soru ve cevabı doğrudan düzenleyip kaydedin.</p></div><select value={faqLanguage} onChange={(event) => setFaqLanguage(event.target.value)} className="field max-w-48">{languages.map((language) => <option key={language.id} value={language.id}>{language.label}</option>)}</select></div>
      <div className="mt-5 space-y-4">{faqs.map((faq) => {
        const editable = localizedFaq(faq, faqLanguage);
        return <div key={faq.id} className="rounded-2xl border border-detail bg-surface p-4">
          <label className="text-sm font-semibold">Soru<input className="field mt-2" value={editable.question || ""} onChange={(event) => updateFaqField(faq, "question", event.target.value)} /></label>
          <label className="mt-3 block text-sm font-semibold">Cevap<textarea className="field mt-2 min-h-24" value={editable.answer || ""} onChange={(event) => updateFaqField(faq, "answer", event.target.value)} /></label>
          <div className="mt-3 flex flex-wrap gap-2">
            <TranslationDraftButton disabled={!faq.question?.trim() || !faq.answer?.trim()} onTranslate={() => translateFaq(faq)} />
            <button onClick={() => saveFaq(faq)} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-cream">Kaydet</button>
            <button onClick={() => deleteFaq(faq.id)} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600">Sil</button>
          </div>
        </div>;
      })}</div>
      <div className="mt-6 rounded-2xl border border-dashed border-brand/35 bg-brand/5 p-4">
        <h3 className="font-semibold">Yeni soru ekle</h3>
        <input className="field mt-3" placeholder="Türkçe soru" value={newFaq.question} onChange={(event) => setNewFaq({ ...newFaq, question: event.target.value })} />
        <textarea className="field mt-3 min-h-24" placeholder="Türkçe cevap" value={newFaq.answer} onChange={(event) => setNewFaq({ ...newFaq, answer: event.target.value })} />
        <div className="mt-3 flex flex-wrap gap-2">
          <TranslationDraftButton disabled={!newFaq.question.trim() || !newFaq.answer.trim()} onTranslate={translateNewFaq} />
          <button onClick={addFaq} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-cream">Soru ekle</button>
        </div>
      </div>
    </div>}
  </section>;
};
